module Sm.Service.Tracking

open System
open Browser.Types

open Fable.DateFunctions
open Fable.Core

open Sm.Core.Text
open Sm.Core.Date
open Sm.Core.Numeric

open Sm.Service.Database

let secondsPerCentile = 100
let secondsPerHour = 60 * 60
let secondsPerDay = secondsPerHour * 24

[<RequireQualifiedAccess>]
type Range =
    | Day of DateOnly
    | Hour of DateOnly * hour:int
    | Centile of DateOnly * hour:int * centile:int

type Seconds =
    {
        Over: int
        Under: int
    }
    static member Zero = { Over = 0; Under = 0 }

type Adjustment =
    | SecondsUnderTime of int
    | SecondsOverTime of int

module Range =
    let centile minute second =
        let m = minute * 60 + second
        m / 100

    let centileOf (date: DateTime) =
        Range.Centile (DateOnly.FromDateTime(date), hour = date.Hour, centile = centile date.Minute date.Second)

    let hourOf (date: DateTime) =
        Range.Hour (DateOnly.FromDateTime(date), hour = date.Hour)

    let dayOf (date: DateTime) =
        Range.Day (DateOnly.FromDateTime(date))

    let tryIncrement = function
        | Range.Day d ->
            let next = d.AddDays(1)
            if next.Month = d.Month then Some (Range.Day next)
            else None

        | Range.Hour (d, h) ->
            if h = 23 then None
            else Some (Range.Hour (d, h + 1))

        | Range.Centile (d, h, c) ->
            if c = 35 then None
            else Some (Range.Centile (d, h, c + 1))

    let start = function
        | Range.Day d -> Range.Day (DateOnly(d.Year, d.Month, 1))
        | Range.Hour (d, _) -> Range.Hour (d, 0)
        | Range.Centile (d, h, _) -> Range.Centile (d, h, 0)

    let contains (value: DateTime) range =
        let day (d: DateOnly) = d.Year = value.Year && d.Month = value.Month && d.Day = value.Day
        let hour d h = day d && value.Hour = h

        match range with
        | Range.Day d -> day d
        | Range.Hour (d, h) -> hour d h
        | Range.Centile (d, h, c) -> (hour d h) && (centile value.Minute value.Second) = c

    let unpack = function
        | Range.Day d -> d.Year, d.Month, d.Day, None, None
        | Range.Hour (d, h) -> d.Year, d.Month, d.Day, Some h, None
        | Range.Centile (d, h, c) -> d.Year, d.Month, d.Day, Some h, Some c

    let toDate = function
        | Range.Day d -> d
        | Range.Hour (d, _) -> d
        | Range.Centile (d, _, _) -> d

    let combine a b range =
        let sum clamp =
            {
                Under = min clamp (a.Under + b.Under)
                Over = min clamp (a.Over + b.Over)
            }

        match range with
        | Range.Day _ -> sum secondsPerDay
        | Range.Hour _ -> sum secondsPerHour
        | Range.Centile _ -> sum secondsPerCentile

type Range with
    member inline range.Date = range |> Range.toDate

module Data =
    type Type = (Range * Seconds) seq
    type Array64 = JS.BigInt64Array

    module Record =
        let epoch = dateUtc 2023 01 01

        type Field = {
            Read: uint64 -> int
            Write: int -> uint64 -> uint64
        }

        let private makeField offset length =
            {
                Read = int << Bitfield64.get offset length
                Write = uint64 >> Bitfield64.set offset length
            }

        let zero = 0UL

        let positiveSeconds = makeField 0 7
        let negativeSeconds = makeField 7 7
        let centile = makeField 14 6
        let offset = makeField 20 20
        let nodeA = makeField 40 8
        let nodeB = makeField 48 8
        let nodeC = makeField 56 8

        let format (clientId: Guid) range seconds =
            match range with
            | Range.Centile (d, h, c) ->
                let nodeByte =
                    let source = clientId.ToByteArray()[13..]
                    fun x -> int (source[x])

                let date = DateTime(d.Year, d.Month, d.Day, h, 0, 0)
                if date < epoch then None
                else
                    let o = (date - epoch).TotalHours |> floor |> int

                    zero
                    |> nodeA.Write (nodeByte 0)
                    |> nodeB.Write (nodeByte 1)
                    |> nodeC.Write (nodeByte 2)
                    |> offset.Write o
                    |> centile.Write c
                    |> positiveSeconds.Write seconds.Under
                    |> negativeSeconds.Write seconds.Over
                    |> int64
                    |> Some
            | _ -> None

        let parse (record: int64) =
            let record = uint64 record

            let o = record |> offset.Read
            let c = record |> centile.Read

            let date = epoch.AddHours(o)
            let range = Range.Centile (DateOnly.FromDateTime(date), date.Hour, c)

            let seconds = {
                Under = record |> positiveSeconds.Read
                Over = record |> negativeSeconds.Read
            }

            range, seconds

    let decode items =
        items
        |> Seq.map Record.parse

    let encode clientId items =
        items
        |> Seq.choose (fun (k, v) -> Record.format clientId k v)

    let pack data: Array64 =
        data
        |> Seq.toArray
        |> JS.Constructors.BigInt64Array.Create

    let unpack (packed: Array64) =
        packed
        |> unbox<int64 array>
        |> seq

    let serialize (data: int64 seq) =
        data
        |> Seq.map string
        |> join " "

    let parse (text: string) =
        text.Split(' ', StringSplitOptions.TrimEntries ||| StringSplitOptions.RemoveEmptyEntries)
        |> Seq.map int64

    let load key (store: IDBObjectStore) =
        promise {
            match! store.get(key) |> result<Array64 option> with
            | None -> return Seq.empty
            | Some packed -> return (unpack packed)
        }

    let save key (data: int64 seq) (store: IDBObjectStore) =
        store.put((pack data), key) |> result<unit>

module Store =
    let update adjustment clamp range state =
        state |> Map.change range (fun record ->
            let s = record |> Option.defaultValue Seconds.Zero
            let current = s.Under + s.Over

            let result =
                match adjustment with
                | SecondsUnderTime x ->
                    let proposed = current + x
                    if proposed < clamp then { s with Under = s.Under + x }
                    else s

                | SecondsOverTime x ->
                    let proposed = current + x
                    if proposed < clamp then { s with Over = s.Over + x }
                    else s

            Some result
        )

    let apply data state =
        let mutable result = state

        data
        |> Seq.iter (function
            | Range.Centile (d, h, _) as centile, seconds ->
                let hour = Range.Hour (d, h)
                let day = Range.Day d

                let applyToResult adj =
                    result <- (centile, result) ||> update adj secondsPerCentile
                    result <- (hour, result) ||> update adj secondsPerHour
                    result <- (day, result) ||> update adj secondsPerDay

                let under = seconds.Under in
                    if under > 0 then applyToResult (SecondsUnderTime under)

                let over = seconds.Over in
                    if over > 0 then applyToResult (SecondsOverTime over)
            | _ -> ()
        )
        result
